import { BannerCardV3 } from '@/components/card/banner-card-v3';
import { CardVariant } from '@/components/card/types';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { DS_HEIGHT } from '@calm/evexia-design-tokens';
import { ProgramContextProps } from '@rhg/hooks/use-program-context/types';
import { sxAppend } from '@rhg/libs/material/sx';
import { SessionCardProps } from '..';
import { BannerCard } from '../../banner-card';
import Card from '../../card';
import { ImageDecorationPosition } from '../../focusable-overlaid-image/types';
import { SessionCardAdditionalAction } from '../additional-action';
import { SessionCardBannerButton } from '../banner-button';
import { IconBottom } from '../icon-bottom';
import { useVariantAgnosticSessionCardProps } from './use-variant-agnostic-session-card-props';
import { useVariantAwareSessionCardProps } from './use-variant-aware-session-card-props';

export type ProgramContextAwareSessionCardProps = Omit<
    SessionCardProps,
    keyof ProgramContextProps
>;

export function ProgramContextAwareSessionCard({
    variant,
    session,
    sessionIndex,
    additionalAction,
    categoryTitle,
    skipSubtitle = false,
    sx,
    contentSx,
    backgroundColor,
    trackProps,
    entireCardButtonProps,
    imageIconTop,
    imageSideRadiusEnabled,
    priority,
    hideFavoriteButton,
    contentTag,
    onPlay,
    progressSource,
    showNarratorAvatar,
    showPlayingIndicator,
    onClick: customOnClick,
    disableViewedTracking,
}: ProgramContextAwareSessionCardProps) {
    const isMobile = useIsMobile();
    const {
        isVideo,
        categoryIcon,
        categoryTitle: displayedCategoryTitle,
        subtitle,
        isLocked,
        durationText,
        program,
        label,
        cardRef,
        titleIcon,
    } = useVariantAgnosticSessionCardProps({
        session,
        sessionIndex,
        categoryTitle,
        trackProps,
        showNarratorAvatar,
        disableViewedTracking,
    });
    const {
        onClick,
        defaultContentSx,
        horizontalSx,
        imageForcedSize,
        focusIcon,
        categoryDescription,
        titleTopSpacing,
        additionalImageDecorations,
    } = useVariantAwareSessionCardProps({
        variant,
        session,
        categoryTitle,
        trackProps,
        onPlay,
        progressSource,
        additionalAction,
        imageIconTop,
        onClick: customOnClick,
    });

    if (variant === CardVariant.BannerV3) {
        return (
            <BannerCardV3
                ref={cardRef}
                title={{
                    text: session?.title ?? '',
                    maxLines: 2,
                    topSpacing: titleTopSpacing,
                    icon: titleIcon,
                }}
                subtitle={{ text: program?.title ?? '' }}
                category={{
                    title: displayedCategoryTitle,
                    titleColor: 'text.sub',
                    icon: categoryIcon,
                    description: categoryDescription,
                }}
                button={{
                    group: (
                        <SessionCardBannerButton
                            variant={CardVariant.BannerV3}
                            session={session}
                            sessionIndex={sessionIndex}
                            categoryTitle={categoryTitle}
                            trackProps={trackProps}
                            onPlay={onPlay}
                            progressSource={progressSource}
                        />
                    ),
                    additionalAction: isMobile ? undefined : (
                        <SessionCardAdditionalAction
                            variant={variant}
                            session={session}
                            additionalAction={additionalAction}
                            hideFavoriteButton={hideFavoriteButton}
                            showPlayingIndicator={showPlayingIndicator}
                        />
                    ),
                }}
                container={{
                    onClick,
                    isClickable: true,
                    'aria-label': label,
                    'data-syntheticid': 'SessionCard',
                    'data-cy': 'GuideCardPlay',
                }}
                content={{
                    tag: contentTag,
                    sx: sxAppend(defaultContentSx, contentSx),
                }}
                sx={sx}
                data-syntheticid="SessionCard"
                image={{
                    // React 19 fix: Use undefined instead of empty string to prevent
                    // "empty src attribute" error. React 19 is stricter about empty src.
                    src: session.image.url || undefined,
                    focusIcon: focusIcon,
                    forcedSize: imageForcedSize,
                    sideRadiusEnabled: imageSideRadiusEnabled ?? true,
                    decorations: [
                        ...additionalImageDecorations,
                        {
                            position: ImageDecorationPosition.TopLeft,
                            children: imageIconTop,
                        },
                    ],
                    props: { priority },
                }}
            />
        );
    }

    if (variant === CardVariant.BannerV2) {
        return (
            <BannerCard
                ref={cardRef}
                isEntireCardClickable={true}
                title={{
                    icon: titleIcon,
                    text: session?.title ?? '',
                    maxLines: 2,
                    topSpacing: titleTopSpacing,
                }}
                label={label}
                subtitle={{ text: program?.title ?? '' }}
                category={{
                    title: displayedCategoryTitle,
                    titleColor: 'text.sub',
                    icon: categoryIcon,
                    description: categoryDescription,
                }}
                button={{
                    group: (
                        <SessionCardBannerButton
                            variant={CardVariant.BannerV2}
                            session={session}
                            sessionIndex={sessionIndex}
                            categoryTitle={categoryTitle}
                            trackProps={trackProps}
                            onPlay={onPlay}
                            progressSource={progressSource}
                        />
                    ),
                    additionalAction: (
                        <SessionCardAdditionalAction
                            variant={variant}
                            session={session}
                            additionalAction={additionalAction}
                            hideFavoriteButton={hideFavoriteButton}
                            showPlayingIndicator={showPlayingIndicator}
                        />
                    ),
                }}
                onClick={onClick}
                content={{
                    tag: contentTag,
                    sx: sxAppend(defaultContentSx, contentSx),
                }}
                sx={sx}
                variant={CardVariant.BannerV2}
                entireCardButtonProps={{
                    'aria-label': program?.title ?? '',
                    tabIndex: -1,
                    'data-cy': 'ProgramViewDetails',
                    ...(entireCardButtonProps ?? {}),
                }}
                data-syntheticid="SessionCard"
                backgroundColor={backgroundColor}
                image={{
                    // React 19 fix: Use undefined instead of empty string to prevent
                    // "empty src attribute" error. React 19 is stricter about empty src.
                    src: session.image.url || undefined,
                    focusIcon: focusIcon,
                    forcedSize: imageForcedSize
                        ? imageForcedSize
                        : DS_HEIGHT.HORIZONTAL_CARD_SIZE,
                    decorations: [
                        {
                            position: ImageDecorationPosition.TopLeft,
                            children: imageIconTop,
                        },
                        {
                            position: ImageDecorationPosition.BottomLeft,
                            children: (
                                <IconBottom
                                    durationText={durationText}
                                    isLocked={isLocked}
                                    isVideo={isVideo}
                                    variant={CardVariant.BannerV2}
                                />
                            ),
                        },
                    ],
                    sideRadiusEnabled: imageSideRadiusEnabled ?? true,
                    props: { priority },
                }}
            />
        );
    }

    return (
        <Card
            ref={cardRef}
            title={{
                text: session.title ?? '',
                maxLines: 2,
                topSpacing: titleTopSpacing,
                icon: titleIcon,
            }}
            label={label}
            variant={variant}
            subtitle={!skipSubtitle ? { text: subtitle } : undefined}
            category={{
                title: displayedCategoryTitle,
                titleColor: 'text.sub',
                icon: categoryIcon,
                description: categoryDescription,
            }}
            button={{
                group:
                    variant === CardVariant.Banner ? (
                        <SessionCardBannerButton
                            variant={variant}
                            session={session}
                            sessionIndex={sessionIndex}
                            categoryTitle={categoryTitle}
                            trackProps={trackProps}
                            onPlay={onPlay}
                            progressSource={progressSource}
                        />
                    ) : undefined,
                additionalAction: (
                    <SessionCardAdditionalAction
                        variant={variant}
                        session={session}
                        additionalAction={additionalAction}
                        hideFavoriteButton={hideFavoriteButton}
                        showPlayingIndicator={showPlayingIndicator}
                    />
                ),
            }}
            onClick={onClick}
            sx={sxAppend(horizontalSx, sx)}
            content={{
                tag: contentTag,
                sx: sxAppend(defaultContentSx, contentSx),
            }}
            isEntireCardClickable={variant !== CardVariant.Banner}
            entireCardButtonProps={{
                ...(entireCardButtonProps ?? {}),
                'data-cy':
                    variant === CardVariant.Banner
                        ? undefined
                        : 'GuideCardPlay',
            }}
            data-syntheticid="SessionCard"
            backgroundColor={backgroundColor}
            image={{
                // React 19 fix: Use undefined instead of empty string to prevent
                // "empty src attribute" error. React 19 is stricter about empty src.
                src: session.image.url || undefined,
                focusIcon: focusIcon,
                forcedSize: imageForcedSize,
                sideRadiusEnabled: imageSideRadiusEnabled ?? false,
                decorations: [
                    {
                        position: ImageDecorationPosition.TopLeft,
                        children: imageIconTop,
                    },
                    {
                        position: ImageDecorationPosition.BottomLeft,
                        children: (
                            <IconBottom
                                durationText={durationText}
                                isLocked={isLocked}
                                isVideo={isVideo}
                                variant={variant}
                            />
                        ),
                    },
                ],
                props: { priority },
            }}
        />
    );
}
